import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { isPraise } from '@/lib/who-match';
import { useTranslation } from 'react-i18next';
import { getOfflineReopenSnapshot, subscribeOfflineReopen } from '@/lib/offline-reopen';
import { getReadingAccessSnapshot, subscribeReadingAccess } from '@/lib/reading-access';
import { Link, Route, Routes, useLocation } from 'react-router-dom';
import Layout from '@/components/Layout';
import ReadingOnlyIndicator from '@/components/ReadingOnlyIndicator';
import { BIBLE_ENABLED } from '@/lib/feature-flags';
import { countBsbBooks } from '@/lib/bible/active';
import { isCoreReadOnly } from '@/lib/core-cutover';
import { useUpdateHold } from '@/hooks/useUpdateHold';
import { useBaseRouteVisitKey, useVerifiedHomeHandoff } from '@/hooks/useOfflineHomeNavigation';
import type { Prayer } from '@/types/prayer';
import styles from './OfflineReopenGate.module.css';

const PAGE_SIZE = 30;

function SavedNotes({ prayer }: { prayer: Prayer }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const end = Math.max(0, prayer.progressHistory.length - page * PAGE_SIZE);
  const entries = prayer.progressHistory.slice(Math.max(0, end - PAGE_SIZE), end);
  return <details>
    <summary>{t('offlineReopen.savedNotes')}</summary>
    {entries.map((entry, index) => <p key={entry.id ?? index} className={styles.words}>{entry.note}</p>)}
    {prayer.progressHistory.length > PAGE_SIZE && <div className={styles.actions}>
      {page > 0 && <button type="button" onClick={() => setPage(page - 1)}>{t('offlineReopen.newer')}</button>}
      {end > PAGE_SIZE && <button type="button" onClick={() => setPage(page + 1)}>{t('offlineReopen.earlier')}</button>}
    </div>}
    {prayer.testimony && <section>
      <h3>{t('offlineReopen.testimony')}</h3>
      <p className={styles.words}>{prayer.testimony}</p>
    </section>}
  </details>;
}

/** Reading-only Home/account content; the public reader is a stable sibling. */
export default function OfflineReopenGate({ children, hideContent = false, onHomeAction, onOwnerRetry }: {
  children: (renderProtected: boolean, coldHandoffOwnerId: string | null) => ReactNode;
  hideContent?: boolean;
  onHomeAction: (prayerId?: string) => void;
  onOwnerRetry?: () => void;
}) {
  const state = useSyncExternalStore(subscribeOfflineReopen, getOfflineReopenSnapshot, getOfflineReopenSnapshot);
  const access = useSyncExternalStore(subscribeReadingAccess, getReadingAccessSnapshot, getReadingAccessSnapshot);
  const visitKey = useBaseRouteVisitKey();
  const [keptVisit, setKeptVisit] = useState<string | null>(null);
  // Preserve the reading visit until owner admission. An idle '/' then
  // returns to real Home automatically; an open interaction keeps the visit
  // until navigation, without closing a sheet or discarding input.
  if (!hideContent && state.phase !== 'pass' && keptVisit !== visitKey) setKeptVisit(visitKey);
  const keepVerifiedVisit = keptVisit === visitKey && state.verifiedOwnerId !== undefined &&
    access.local?.ownerId === state.verifiedOwnerId;
  const homeHandoff = useVerifiedHomeHandoff(keepVerifiedVisit,
    state.phase === 'pass' && !access.restricted && access.ownerId === state.verifiedOwnerId, visitKey);
  const showSaved = !hideContent && (state.phase !== 'pass' || (keepVerifiedVisit && !homeHandoff));
  useUpdateHold(state.phase === 'checking' || state.reconnecting, 'offline-reopen');
  return <>
    {showSaved && <ReadingOnlyHome phase={state.phase === 'pass' ? 'reading' : state.phase}
      onHomeAction={onHomeAction} onRetry={state.phase === 'pass' ? onOwnerRetry : undefined} />}
    {state.phase === 'pass' && children(!showSaved, state.verifiedOwnerId ?? null)}
  </>;
}

export function ReadingOnlyHome({ phase = 'unavailable', onHomeAction, onRetry }: {
  phase?: 'checking' | 'reading' | 'unavailable';
  onHomeAction?: (prayerId?: string) => void;
  onRetry?: () => void;
}) {
  const access = useSyncExternalStore(subscribeReadingAccess, getReadingAccessSnapshot, getReadingAccessSnapshot);
  const { t } = useTranslation();
  const location = useLocation();
  const [page, setPage] = useState(0);
  const [cachedBible, setCachedBible] = useState(false);
  useEffect(() => {
    if (!BIBLE_ENABLED) return;
    let disposed = false;
    // The restricted Home offers only public books already saved on this device.
    void countBsbBooks().then((count) => {
      if (!disposed) setCachedBible(count > 0);
    }).catch(() => { if (!disposed) setCachedBible(false); });
    return () => { disposed = true; };
  }, [phase]);
  const writingAllowed = !access.restricted && !isCoreReadOnly();
  const saved = access.local?.prayers ?? [];
  const currentPage = Math.min(page, Math.max(0, Math.ceil(saved.length / PAGE_SIZE) - 1));
  const prayers = saved.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
  const lastRead = access.local?.lastRead;
  const biblePath = lastRead ? `/bible/${lastRead.bookSlug}/${lastRead.chapter}` : '/bible';
  return <Routes>
    <Route element={<Layout />}>
      <Route path="*" element={
        <section className={styles.root} data-offline-reopen={phase}>
          <div className={styles.column}>
            {location.pathname !== '/' && <Link className={styles.back} to="/">{t('bible.back')}</Link>}
            <ReadingOnlyIndicator />
            <header className={styles.header}>
              <h1>{t('offlineReopen.title')}</h1>
              <nav className={styles.navigation}>
                {BIBLE_ENABLED && cachedBible && <Link className={styles.bible} to={biblePath}>{t('offlineReopen.readBible')}</Link>}
                {onRetry && <button type="button" className={styles.retry} onClick={onRetry}>{t('accountLoad.retry')}</button>}
                {writingAllowed && onHomeAction && <button type="button" data-saved-prayer-write
                  onClick={() => onHomeAction()}>{t('offlineReopen.write')}</button>}
              </nav>
            </header>
            {prayers.length === 0 ? phase !== 'checking' && <p className={styles.help}>{t(
              phase === 'unavailable' ? 'offlineReopen.unavailable' : 'offlineReopen.empty',
            )}</p> : <ul className={styles.list}>
              {prayers.map((prayer) => <li key={prayer.id} className={styles.prayer}>
                <div className={styles.prayerHeading}>
                  <h2>{isPraise(prayer) ? t('entry.praiseLabel') : prayer.parsed?.who || t('offlineReopen.prayer')}</h2>
                  <div className={styles.prayerActions}>
                    {writingAllowed && onHomeAction && <button type="button" data-saved-prayer-edit={prayer.id}
                      onClick={() => onHomeAction(prayer.id)}>{t('detail.edit')}</button>}
                  </div>
                </div>
                <p className={styles.words}>{prayer.rawText || t('offlineReopen.recordingPending')}</p>
                {prayer.scriptureAnchor && <p className={styles.reference}>{prayer.scriptureAnchor.reference}</p>}
                {(prayer.progressHistory.length > 0 || prayer.testimony) && <SavedNotes prayer={prayer} />}
              </li>)}
            </ul>}
            {saved.length > PAGE_SIZE && <nav className={styles.actions} aria-label={t('offlineReopen.pages')}>
              {currentPage > 0 && <button type="button" onClick={() => setPage(currentPage - 1)}>{t('offlineReopen.newer')}</button>}
              {(currentPage + 1) * PAGE_SIZE < saved.length && <button type="button" onClick={() => setPage(currentPage + 1)}>{t('offlineReopen.earlier')}</button>}
            </nav>}
          </div>
        </section>
      } />
    </Route>
  </Routes>;
}
